import { useQuery } from "@tanstack/react-query";
import { doc, getDoc } from "firebase/firestore";
import { getDb } from "./firebase";

/**
 * The subset of `config/appConfig` the public site actually renders.
 *
 * Deliberately narrower than the admin editor's `AppConfig` (`lib/admin-api.ts`):
 * - `termsUrl` / `privacyPolicyUrl` are absolute `https://asistent-auto.ro/...`
 *   links meant for the Android app. The web app owns those pages, so it links
 *   them with react-router `<Link to="/termeni">` — routing through the config
 *   URLs would turn every legal cross-reference into a full page reload.
 * - `contactPhone`, `vignetteRenew`, `rcaGroupamaDeclaration`, `forceUpdateVersion`,
 *   `hasLabelFeature` have no web surface today. Add a field here when a page
 *   renders it, not before.
 */
export type PublicAppConfig = {
  companyName: string;
  companyRegistrationNumber: string;
  companyUniqueNumber: string;
  address: string;
  contactEmail: string;
};

/**
 * Served until `config/appConfig` resolves, and per-key whenever the doc is
 * missing or the field is blank/wrong-typed.
 *
 * These must stay in sync with what an admin would set in `/admin/setari` —
 * they are what the legal pages display for the first few hundred ms of every
 * visit, and what they fall back to if Firestore is unreachable. A page must
 * never render an empty company name or a `mailto:` with nothing after the colon.
 */
export const APP_CONFIG_DEFAULTS: PublicAppConfig = {
  companyName: "SC Asistent Digital SRL",
  companyRegistrationNumber: "J1/265/22.03.2018",
  companyUniqueNumber: "39066817",
  address: "Sat Cut, Jud. Alba, nr. 652, Cod Poștal 517206",
  contactEmail: "suport.asistent.auto@gmail.com",
};

export const DEFAULT_CONTACT_EMAIL = APP_CONFIG_DEFAULTS.contactEmail;

/**
 * Read the public fields out of a raw `config/appConfig` doc, falling back
 * per-key so one bad field can't blank out the rest.
 *
 * Sibling of `normalizeAppConfig` in `src/views/admin/setari/AppConfigSection.tsx`,
 * with the opposite fallback policy on purpose: the editor fails a missing key
 * to `""` so the operator sees an empty input to fill, while the public site
 * fails it to a real value so a visitor never sees a blank.
 */
export function publicAppConfigFromDoc(raw: Record<string, unknown> | undefined): PublicAppConfig {
  const pick = (key: keyof PublicAppConfig): string => {
    const value = raw?.[key];
    if (typeof value !== "string") return APP_CONFIG_DEFAULTS[key];
    const trimmed = value.trim();
    return trimmed === "" ? APP_CONFIG_DEFAULTS[key] : trimmed;
  };
  return {
    companyName: pick("companyName"),
    companyRegistrationNumber: pick("companyRegistrationNumber"),
    companyUniqueNumber: pick("companyUniqueNumber"),
    address: pick("address"),
    contactEmail: pick("contactEmail"),
  };
}

// Exported so the build-time prerenderer can warm the same query key before
// rendering (scripts/prerender.mjs via src/entry-server.tsx). Components should
// keep using `useAppConfig()`.
export async function readPublicAppConfig(): Promise<PublicAppConfig> {
  const snap = await getDoc(doc(getDb(), "config", "appConfig"));
  return publicAppConfigFromDoc(snap.exists() ? (snap.data() as Record<string, unknown>) : undefined);
}

/**
 * Company identity + contact details configured in `/admin/setari`
 * (`config/appConfig`), for the marketing and legal pages.
 *
 * One-shot read, not realtime — these change about never, and a live listener
 * on every marketing page would buy nothing. TanStack Query dedupes the read
 * across pages and caches it for the session; `config/**` is publicly readable
 * in `firestore.rules`, so this works signed out.
 *
 * Always returns usable values (see `APP_CONFIG_DEFAULTS`) — while loading and
 * on any read error — so callers never need a loading branch.
 */
export function useAppConfig(): PublicAppConfig {
  const { data } = useQuery({
    queryKey: ["appConfig", "public"],
    queryFn: readPublicAppConfig,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
  return data ?? APP_CONFIG_DEFAULTS;
}

/** Shorthand for the common case — the support address, e.g. for a `mailto:`. */
export function useContactEmail(): string {
  return useAppConfig().contactEmail;
}
